import { c as _c } from "react/compiler-runtime";
import { postOrgPlaybooksEditSessionsByEditSessionIdCommitMutation } from '@onefootprint/fetch-dashboard';
import type { PlaybookSettings } from '@onefootprint/request-types/dashboard';
import { Button, Checkbox, Dialog, Form, useToast } from '@onefootprint/ui';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useSession from '@/hooks/use-session';
import useEdit from '../../hooks/use-edit';
import useHiddenNodeErrors from '../../hooks/use-hidden-node-errors';
import useStore from '../../hooks/use-store';
export type CreateDialogProps = {
  onDone: (playbookId: string) => void;
};
const CreateDialog = ({
  onDone
}: CreateDialogProps) => {
  const {
    t
  } = useTranslation('playbook-builder', {
    keyPrefix: 'header.createDialog'
  });
  const [isOpen, setIsOpen] = useState(false);
  const editMutation = useEdit();
  const commitMutation = useMutation(postOrgPlaybooksEditSessionsByEditSessionIdCommitMutation());
  const store = useStore();
  const {
    toast
  } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    data: {
      user
    }
  } = useSession();
  const {
    data: {
      settings
    },
    visibleBanners
  } = store;
  const {
    hasHiddenErrors,
    reveal: revealHiddenErrors
  } = useHiddenNodeErrors();
  const hasErrors = visibleBanners.some(b => b.type === 'error');
  const canSkipValidation = !!(user?.isRiskOpsEmployee || user?.isCxOpsEmployee);
  const handleOpen = () => {
    if (hasHiddenErrors) return revealHiddenErrors();
    setIsOpen(true);
  };
  const handleClose = () => {
    setIsOpen(false);
  };
  const handleSubmit = async (data: FormData) => {
    if (!store.data.sessionId) return;
    try {
      setIsSubmitting(true);
      await editMutation.mutateAsync({
        kind: 'update_settings',
        name: data.name
      });
      await publish(store.data.sessionId, data.skipValidation);
      setIsOpen(false);
      if (store.data.playbookId) {
        onDone(store.data.playbookId);
      }
    } catch (_) {} finally {
      setIsSubmitting(false);
    }
  };
  const publish = async (sessionId: string, skipValidation: boolean) => {
    try {
      await commitMutation.mutateAsync({
        path: {
          editSessionId: sessionId
        },
        body: {
          action: 'publish',
          skipValidation
        }
      });
      toast({
        title: t($_1 => $_1.feedback.saveSuccess.title),
        description: t($_2 => $_2.feedback.saveSuccess.description)
      });
    } catch (__0) {
      toast({
        variant: 'error',
        title: t($ => $.feedback.saveError.title),
        description: t($_0 => $_0.feedback.saveError.description)
      });
    }
  };
  return <>
      <Button onClick={handleOpen} size="compact" disabled={!hasHiddenErrors && !canSkipValidation && hasErrors}>
        {t($_3 => $_3.trigger)}
      </Button>
      <Dialog title={t($_4 => $_4.title)} open={isOpen} onClose={handleClose} size="compact" primaryButton={{
      form: 'playbook-name-form',
      label: t($_5 => $_5.cta),
      loading: isSubmitting,
      type: 'submit'
    }} secondaryButton={{
      disabled: isSubmitting,
      label: t($_6 => $_6.cancel),
      onClick: handleClose
    }}>
        <NameForm onSubmit={handleSubmit} playbookSettings={settings} showSkipValidation={hasErrors && canSkipValidation} />
      </Dialog>
    </>;
};
type FormData = {
  name: string;
  skipValidation: boolean;
};
type NameFormProps = {
  onSubmit: (data: FormData) => void;
  playbookSettings?: PlaybookSettings;
  showSkipValidation: boolean;
};
const NameForm = t0 => {
  const $ = _c(29);
  const {
    onSubmit,
    playbookSettings,
    showSkipValidation
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "header.createDialog"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("playbook-builder", t1);
  const t2 = playbookSettings?.name || "";
  let t3;
  if ($[1] !== t2) {
    t3 = {
      defaultValues: {
        name: t2,
        skipValidation: false
      }
    };
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const formMethods = useForm(t3);
  const {
    register,
    handleSubmit,
    formState: t4
  } = formMethods;
  const {
    errors
  } = t4;
  let t5;
  if ($[3] !== handleSubmit || $[4] !== onSubmit) {
    t5 = handleSubmit(onSubmit);
    $[3] = handleSubmit;
    $[4] = onSubmit;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== t) {
    t6 = t(_temp);
    $[6] = t;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== t) {
    t7 = t(_temp2);
    $[8] = t;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== t) {
    t8 = t(_temp3);
    $[10] = t;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== t8) {
    t9 = {
      required: t8
    };
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== t6 || $[15] !== t7 || $[16] !== t9) {
    t10 = <Form.Input name="name" size="compact" label={t6} placeholder={t7} rules={t9} />;
    $[14] = t6;
    $[15] = t7;
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== errors || $[19] !== register || $[20] !== showSkipValidation) {
    t11 = showSkipValidation && <Checkbox label="Save graph with errors (Employees only)" hasError={!!errors.skipValidation} hint={errors.skipValidation?.message} {...register("skipValidation", {
      required: "This graph has errors. You must select this option to save."
    })} />;
    $[18] = errors;
    $[19] = register;
    $[20] = showSkipValidation;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== t10 || $[23] !== t11 || $[24] !== t5) {
    t12 = <form className="flex flex-col gap-6" id="playbook-name-form" onSubmit={t5}>{t10}{t11}</form>;
    $[22] = t10;
    $[23] = t11;
    $[24] = t5;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== formMethods || $[27] !== t12) {
    t13 = <FormProvider {...formMethods}>{t12}</FormProvider>;
    $[26] = formMethods;
    $[27] = t12;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  return t13;
};
export default CreateDialog;
function _temp($) {
  return $.name.label;
}
function _temp2($_0) {
  return $_0.name.placeholder;
}
function _temp3($_1) {
  return $_1.name.errors.required;
}